import { Body1, Button, Subtitle2, Divider, mergeClasses } from "@fluentui/react-components";
import React from "react";
import { useStyles } from "./useStyles";

type CartEntryProps = {
  amount?: string;
  cafeId?: string;
  itemId?: string;
  label?: string;
  accessibleLabel?: string;
  subTitle?: string;
  customInstructions?: string;
  divStatus?: boolean;
  amountClassName?: string;
  id?: string;
  totalAmountClassName?: string;
  onRemove?: (cafeId: string, itemId: string) => void;
  onEdit?: (cafeId: string, itemId: string, id: string) => void;
  removeButtonRef?: React.RefObject<HTMLButtonElement>;
};

export const CartEntry = ({
  amount,
  cafeId,
  itemId,
  label,
  accessibleLabel,
  subTitle,
  customInstructions,
  divStatus,
  amountClassName,
  totalAmountClassName,
  id,
  onRemove,
  onEdit,
  removeButtonRef,
}: CartEntryProps) => {
  const styles = useStyles();
  const ariaBaseLabel = accessibleLabel ?? label ?? "Item";
  return (
    <>
      <div className={styles.container} data-testid="CartEntry">
        <div className={styles.info}>
          <Subtitle2 wrap truncate className={totalAmountClassName}>
            <span aria-hidden={accessibleLabel && accessibleLabel !== label ? "true" : undefined}>
              {label}
            </span>
            {accessibleLabel && accessibleLabel !== label && (
              <span className={styles.visuallyHidden}>{accessibleLabel}</span>
            )}
          </Subtitle2>
          <Body1 className={styles.subTitle} wrap truncate>
            {subTitle}
          </Body1>
          <Body1 className={styles.customInstructions}>{customInstructions}</Body1>
          <div className={styles.buttonsContainer}>
            {/* DOM order here must match the visual order (Edit, then Remove). The container
                previously used flexDirection: row-reverse with the buttons in Remove-then-Edit
                DOM order to flip them visually, but a visual order that diverges from DOM order
                is a known VoiceOver/WebKit bug: swipe navigation can skip the reordered elements
                entirely and touch-to-activate hit-testing can fail to fire a click. Keeping DOM
                order and visual order in sync (see useStyles.ts's plain "row" direction) fixes
                both. */}
            {onEdit && cafeId && itemId && id && (
              <Button
                aria-label={`Edit ${ariaBaseLabel}`}
                appearance="transparent"
                className={styles.editButton}
                onClick={() => onEdit(cafeId, itemId, id)}
                data-testid="editItemBtn"
              >
                Edit
              </Button>
            )}
            {onRemove && cafeId && id && (
              <Button
                aria-label={`Remove ${ariaBaseLabel} from cart`}
                appearance="transparent"
                className={styles.removeButton}
                onClick={() => onRemove(cafeId, id)}
                data-testid="removeItemBtn"
                ref={removeButtonRef}
              >
                Remove
              </Button>
            )}
          </div>
        </div>

        <Subtitle2 className={mergeClasses(amountClassName, totalAmountClassName)}>
          {amount}
        </Subtitle2>
      </div>
      {divStatus && <Divider />}
    </>
  );
};
